/**
 * SparklineChart Component
 * Compact inline trend visualization
 */

import Plot from 'react-plotly.js'
import { Data, Layout } from 'plotly.js'
import clsx from 'clsx'

interface SparklineChartProps {
  data: number[]
  color?: string
  height?: number
  width?: number | string
  showEndpoint?: boolean
  showArea?: boolean
  showMinMax?: boolean
  positiveColor?: string
  negativeColor?: string
  className?: string
}

export default function SparklineChart({
  data,
  color = '#3b82f6',
  height = 30,
  width = '100%',
  showEndpoint = true,
  showArea = false,
  showMinMax = false,
  positiveColor = '#10b981',
  negativeColor = '#ef4444',
  className
}: SparklineChartProps) {
  if (data.length === 0) {
    return (
      <div
        className={clsx('flex items-center justify-center text-xs text-surface-500', className)}
        style={{ height, width }}
      >
        No data
      </div>
    )
  }

  const xValues = Array.from({ length: data.length }, (_, i) => i)
  const minValue = Math.min(...data)
  const maxValue = Math.max(...data)
  const lastValue = data[data.length - 1]
  const firstValue = data[0]
  const trend = lastValue - firstValue

  // Determine color based on trend
  const lineColor = trend >= 0 ? (positiveColor || color) : (negativeColor || color)

  const traces: Data[] = [
    {
      type: 'scatter',
      x: xValues,
      y: data,
      mode: showEndpoint ? 'lines+markers' : 'lines',
      fill: showArea ? 'tozeroy' : undefined,
      fillcolor: showArea
        ? lineColor.replace(')', ', 0.1)').replace('rgb', 'rgba')
        : undefined,
      line: {
        color: lineColor,
        width: 1.5,
        shape: 'spline',
        smoothing: 0.5
      },
      marker: showEndpoint ? {
        size: xValues.map((_, i) => i === data.length - 1 ? 6 : 0),
        color: lineColor
      } : undefined,
      hoverinfo: 'y',
      hovertemplate: '%{y:.1f}<extra></extra>'
    }
  ]

  // Add min/max markers
  if (showMinMax) {
    const minIndex = data.indexOf(minValue)
    const maxIndex = data.indexOf(maxValue)

    traces.push({
      type: 'scatter',
      x: [minIndex, maxIndex],
      y: [minValue, maxValue],
      mode: 'markers',
      marker: {
        size: 4,
        color: [negativeColor, positiveColor],
        symbol: ['triangle-down', 'triangle-up']
      },
      hoverinfo: 'skip',
      showlegend: false
    } as Data)
  }

  const layout: Partial<Layout> = {
    height,
    margin: { t: 2, r: 2, b: 2, l: 2 },
    paper_bgcolor: 'transparent',
    plot_bgcolor: 'transparent',
    xaxis: {
      visible: false,
      showgrid: false,
      zeroline: false,
      range: [-0.5, data.length - 0.5]
    },
    yaxis: {
      visible: false,
      showgrid: false,
      zeroline: false,
      range: [minValue - (maxValue - minValue) * 0.1, maxValue + (maxValue - minValue) * 0.1]
    },
    showlegend: false,
    hovermode: 'closest'
  }

  const config = {
    displayModeBar: false,
    responsive: true,
    staticPlot: false
  }

  return (
    <div
      className={clsx('inline-flex items-center', className)}
      style={{ width, height }}
    >
      <Plot
        data={traces}
        layout={layout}
        config={config}
        style={{ width: '100%', height: '100%' }}
      />
    </div>
  )
}

/**
 * SparklineWithLabel Component
 * Sparkline with value label for table cells
 */
interface SparklineWithLabelProps extends SparklineChartProps {
  value: number
  label?: string
  unit?: string
  labelPosition?: 'left' | 'right'
}

export function SparklineWithLabel({
  value,
  label,
  unit,
  labelPosition = 'right',
  ...sparklineProps
}: SparklineWithLabelProps) {
  const valueDisplay = (
    <div className="flex flex-col">
      <span className="text-sm font-mono font-semibold text-surface-200">
        {value.toFixed(1)}{unit}
      </span>
      {label && (
        <span className="text-xs text-surface-400">{label}</span>
      )}
    </div>
  )

  return (
    <div className="flex items-center gap-2">
      {labelPosition === 'left' && valueDisplay}
      <SparklineChart width={60} height={24} {...sparklineProps} />
      {labelPosition === 'right' && valueDisplay}
    </div>
  )
}
